import { zodResolver } from '@hookform/resolvers/zod';
import { LoginRequestSchema } from '@invoice/shared/schemas';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useLogin } from '@/lib/queries/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const LoginSearchSchema = z.object({
  redirect: z.string().optional(),
});

type LoginFormValues = z.infer<typeof LoginRequestSchema>;

export const Route = createFileRoute('/login')({
  validateSearch: (search) => LoginSearchSchema.parse(search),
  component: LoginPage,
});

function LoginPage() {
  const { t, setLanguage } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const loginMutation = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const loginFormSchema = useMemo(
    () =>
      LoginRequestSchema.extend({
        email: z.string().email(t('login.invalidEmail')).toLowerCase(),
        password: z.string().min(1, t('login.passwordRequired')),
      }),
    [t],
  );
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
  });

  const redirectTarget =
    typeof search.redirect === 'string' && search.redirect.startsWith('/')
      ? search.redirect
      : '/invoices';

  const handleSubmit: SubmitHandler<LoginFormValues> = async (values) => {
    const result = await loginMutation.mutateAsync(values);
    if (result.mfaRequired) {
      form.setError('root', {
        message: t('login.mfaUnsupported'),
      });
      return;
    }

    setLanguage(result.user.language);
    await queryClient.invalidateQueries();
    void navigate({ to: redirectTarget });
  };

  const submitError =
    form.formState.errors.root?.message ??
    (loginMutation.error instanceof ApiError || loginMutation.error instanceof Error
      ? loginMutation.error.message
      : null);

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-md flex-col justify-center">
      <Card>
        <CardHeader>
          <CardTitle>{t('login.title')}</CardTitle>
          <CardDescription>{t('login.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
            noValidate
          >
            <fieldset disabled={loginMutation.isPending} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="login-email">{t('login.email')}</Label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  {...form.register('email')}
                  aria-invalid={form.formState.errors.email ? true : undefined}
                />
                {form.formState.errors.email ? (
                  <p className="text-xs text-destructive" role="alert">
                    {form.formState.errors.email.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="login-password">{t('login.password')}</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="pr-10"
                    {...form.register('password')}
                    aria-invalid={form.formState.errors.password ? true : undefined}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 inline-flex w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    onClick={() => setShowPassword((current) => !current)}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" aria-hidden />
                    ) : (
                      <Eye className="size-4" aria-hidden />
                    )}
                  </button>
                </div>
                {form.formState.errors.password ? (
                  <p className="text-xs text-destructive" role="alert">
                    {form.formState.errors.password.message}
                  </p>
                ) : null}
              </div>

              {submitError ? (
                <p className="text-sm text-destructive" role="alert">
                  {submitError}
                </p>
              ) : null}

              {import.meta.env.DEV ? (
                <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  {t('login.seedUser')} <code>admin@example.com</code> / <code>changeme123!</code>
                </p>
              ) : null}

              <Button type="submit" disabled={loginMutation.isPending}>
                {loginMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <LogIn className="size-4" aria-hidden />
                )}
                {t('login.submit')}
              </Button>
            </fieldset>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
